import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { StoryCard, StoryCardStatus, useStoryCardsFeedQuery } from '../../../services';
import { getCssVar, IconLabel, layoutPath } from '../../../shared';
import { DisruptionCard } from './DisruptionCard';

import classes from './Disruption.module.scss';

interface ConfigDisruptionStatusIcon {
  tagId: number;
  iconUrl: string;
}

export interface ConfigDisruptionWidget {
  title: string;
  storyId: number;
  cardsLimit: number;
  statusIcons: ConfigDisruptionStatusIcon[];
}

interface DisruptionProps {
  config: ConfigDisruptionWidget;
}

export const Disruption: FunctionComponent<DisruptionProps> = memo(({ config }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { title, storyId, cardsLimit, statusIcons } = config;

  const { data, isFetching } = useStoryCardsFeedQuery(
    {
      storyId,
      size: cardsLimit,
      filter: { onlyCompletedMedia: true, status: [StoryCardStatus.PUBLISHED] },
    },
    { skip: !storyId }
  );

  const cards = useMemo(() => data?.items ?? [], [data?.items]);

  const getIconUrl = useCallback(
    (card: StoryCard): string | undefined =>
      statusIcons.find(({ tagId }) => tagId === card.externalStatusTags[0]?.id)?.iconUrl,
    [statusIcons]
  );

  if (!storyId) {
    return null;
  }

  if (isFetching) {
    return (
      <div className={classes['disruption__cards-loader-wrapper']}>
        <Skeleton className={classes['disruption__cards-loader']} count={cardsLimit} />
      </div>
    );
  }

  if (!cards.length) {
    return null;
  }

  return (
    <div className={classes['disruption']}>
      <div className={classes['disruption__header']}>
        <h2 className={classes['disruption__header-title']}>{title}</h2>
        <IconLabel
          iconSize={18}
          iconId={'right-arrow'}
          label={t('common.view-all')}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
          onClick={() => navigate(layoutPath(`/storyTab/${storyId}/items`))}
          labelFirst
        />
      </div>
      {cards.map((card) => (
        <DisruptionCard key={card.id} card={card} iconUrl={getIconUrl(card)} />
      ))}
    </div>
  );
});
